import React, { useEffect, useState, useRef } from "react";
import ProductCard from "./ProductCard";
import { API_BASE } from "../../utils/api";
import Pagination from "../UI/Pagination";

const ITEMS_PER_PAGE = 15;

export default function FrequentProducts({ idCustomer, idShipTo, t }) {
    const [products, setProducts] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // Refs para controlar la restauración
    const isInitialMount = useRef(true);
    const isRestored = useRef(false);
    // const scrollRestored = useRef(false);

    // Restaurar página guardada al montar el componente
    useEffect(() => {
        const savedPage = sessionStorage.getItem("frequent_products_page");
        //  console.log("Restaurando página de productos frecuentes:", savedPage);

        if (savedPage && savedPage !== "null" && savedPage !== "undefined" && savedPage !== "1") {
            setCurrentPage(Number(savedPage));
            //      console.log("Página de productos frecuentes restaurada a:", savedPage);
        }

        isRestored.current = true;
    }, []);

    // Si cambia el cliente o el punto de envío, reseteamos a la página 1
    useEffect(() => {
        if (!idCustomer) return;

        // Solo resetear si no es la restauración inicial
        if (!isInitialMount.current && isRestored.current) {
            //   console.log("Cliente cambiado, reseteando página a 1");
            setCurrentPage(1);
            // Limpiar página guardada cuando cambia el cliente
            sessionStorage.removeItem("frequent_products_page");
        }
    }, [idCustomer, idShipTo]);

    // Guardar página en sessionStorage cuando cambia
    useEffect(() => {
        if (!isInitialMount.current && isRestored.current) {
            sessionStorage.setItem("frequent_products_page", currentPage);
            //    console.log("Guardando página de productos frecuentes:", currentPage);
        }
    }, [currentPage]);

    // Cargamos datos cuando cambia el cliente, punto de envío o la página actual
    useEffect(() => {
        if (!idCustomer) return;

        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (isRestored.current) {
                loadFrequentProducts();
            }
        } else {
            loadFrequentProducts();
        }
    }, [idCustomer, idShipTo, currentPage]);

    const loadFrequentProducts = async () => {
        setLoading(true);
        try {
            const offset = (currentPage - 1) * ITEMS_PER_PAGE;
            const res = await fetch(
                `${API_BASE}/inventary-ultra/api/ws/frequently_products?id_customer=${idCustomer}&id_ship_to=${idShipTo}&limit=${ITEMS_PER_PAGE}&offset=${offset}`
            );

            const data = await res.json();
            setProducts(data.products || []);
            setTotalRecords(data.totalRecords || 0);
        } catch (err) {
            console.error("Error cargando productos frecuentes:", err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // // Restaurar scroll después de cargar productos
    // useEffect(() => {
    //     if (!loading && products.length > 0 && isRestored.current && !scrollRestored.current) {
    //         const savedScroll = sessionStorage.getItem("frequent_products_scroll");
    //         if (savedScroll && savedScroll !== "null" && savedScroll !== "undefined") {
    //             setTimeout(() => {
    //                 window.scrollTo({ top: Number(savedScroll), behavior: 'auto' });
    //                 //  console.log("Scroll restaurado a:", savedScroll);
    //                 scrollRestored.current = true;
    //                 // No eliminamos el scroll inmediatamente por si se necesita después
    //             }, 100);
    //         } else {
    //             scrollRestored.current = true;
    //         }
    //     }
    // }, [loading, products]);

    const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE);

    const handlePageChange = (page) => {
        // Guardar scroll antes de cambiar de página
        sessionStorage.setItem("frequent_products_scroll", window.scrollY);
        setCurrentPage(page);
    };

    return (
        <div className="mt-4">
            <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-brand-primary">
                    mis {totalRecords} {t.dashboardProducFrec}
                </h2>
            </div>

            <div className="flex items-center justify-between gap-4 mb-0 bg-blue-50/50 p-2 rounded-md border border-blue-100/50 w-full -mt-4">
                {/* Lado Izquierdo: Icono y Texto */}
                <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center bg-blue-500 text-white text-[12px] font-bold rounded-full min-w-[16px] h-4">
                        i
                    </span>
                    <h2 className="text-[12px] font-medium text-blue-700 leading-tight flex items-center">
                        Pase el cursor sobre
                        <span className="flex flex-col ml-1 text-gray-700">
                            <span>Cantidad</span>
                            <span className="leading-[10px]">sugerida</span>
                        </span>
                        <span className="ml-1">para ver el análisis estadístico de cada producto.</span>
                    </h2>
                </div>

                {/* Lado Derecho: Paginador */}
                {totalPages > 1 && (
                    <div className="flex-shrink-0">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            <div className="border-b-2 border-gray-300 w-56 mb-5"></div>

            {loading ? (
                <div className="flex justify-center py-10 text-gray-400">Cargando...</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 min-h-[300px]">
                        {products.length === 0 ? (
                            <p className="text-gray-400 text-sm col-span-full italic">
                                Aún no hay productos frecuentes…
                            </p>
                        ) : (
                            products.map((p) => (
                                <ProductCard
                                    key={p.idProduct}
                                    product={p}
                                    t={t}
                                    fontSize="text-[11px]"
                                    px="px-0"
                                    highlighted={false}
                                    // Props específicos para productos frecuentes
                                    categoryId="frequent" // Identificador para saber que viene de productos frecuentes
                                    selectedGroups={null}
                                    currentPage={currentPage}
                                    hasProdFreq={false}
                                />
                            ))
                        )}
                    </div>

                    {/* Paginador inferior centrado */}
                    {totalPages > 1 && (
                        <div className="mt-8 flex justify-center">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}